import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/i18n";
import { useCategoryLabel } from "@/i18n/categories";

const ALL = "__all__";

export const SearchPanel = ({ locations, value, onSearch, dark = false }) => {
  const t = useT();
  const catLabel = useCategoryLabel();
  const [keyword, setKeyword] = useState(value?.q || "");
  const [state, setState] = useState(value?.state || ALL);
  const [district, setDistrict] = useState(value?.district || ALL);
  const [category, setCategory] = useState(value?.category || ALL);

  const prevState = useRef(state);
  useEffect(() => {
    if (prevState.current !== state) {
      prevState.current = state;
      setDistrict(ALL);
    }
  }, [state]);

  const districts = state !== ALL ? locations?.states?.[state] || [] : [];

  const submit = (e) => {
    e.preventDefault();
    onSearch({
      q: keyword.trim(),
      state: state === ALL ? "" : state,
      district: district === ALL ? "" : district,
      category: category === ALL ? "" : category,
    });
  };

  return (
    <form
      onSubmit={submit}
      data-testid="search-panel"
      className={`grid gap-3 rounded-lg border p-4 sm:p-5 ${
        dark ? "border-white/15 bg-white/95 shadow-2xl" : "border-border bg-card shadow-sm"
      } md:grid-cols-12`}
    >
      <div className="md:col-span-4">
        <Input
          data-testid="search-keyword-input"
          placeholder={t("search.keyword")}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          className="h-11"
        />
      </div>

      <div className="md:col-span-3">
        <Select value={state} onValueChange={setState}>
          <SelectTrigger className="h-11" data-testid="search-state-select">
            <SelectValue placeholder={t("search.state")} />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value={ALL}>{t("search.allStates")}</SelectItem>
            {Object.keys(locations?.states || {}).map((s) => (
              <SelectItem key={s} value={s} data-testid={`state-option-${s}`}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="md:col-span-2">
        <Select value={district} onValueChange={setDistrict} disabled={state === ALL}>
          <SelectTrigger className="h-11" data-testid="search-district-select">
            <SelectValue placeholder={t("search.district")} />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value={ALL}>{t("search.allDistricts")}</SelectItem>
            {districts.map((d) => (
              <SelectItem key={d} value={d} data-testid={`district-option-${d}`}>
                {d}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="md:col-span-2">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-11" data-testid="search-category-select">
            <SelectValue placeholder={t("search.category")} />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value={ALL}>{t("search.allCategories")}</SelectItem>
            {(locations?.categories || []).map((c) => (
              <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>
                {catLabel(c)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button
        type="submit"
        data-testid="search-submit-button"
        className="h-11 bg-primary text-primary-foreground transition-colors duration-200 hover:bg-[#0052CC] md:col-span-1"
      >
        <Search className="h-4 w-4" />
        <span className="ml-2 md:hidden">{t("search.submit")}</span>
      </Button>
    </form>
  );
};
